import { EditOutlined } from '@ant-design/icons'
import { App, Badge, Empty, Avatar, Button, Card, Collapse, Divider, Flex, Form, Input, Modal, Skeleton, Tabs, Tag, Tooltip, Typography } from 'antd'
import { useCallback, useState } from 'react'
import Copilot5V from './Copilot5V'
import { emptyCopilotTabsTemplateData, buildCopilotTabsTemplateData, extractInsightJson } from './copilotTabsTemplateData'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import { useOrderStore } from '@/stores/OrderStore'

const TABS_LAYOUT_CSS = `
.copilot-tabs > .ant-tabs-content-holder { min-height: 0; flex: 1; overflow: auto; }
.copilot-tabs > .ant-tabs-content-holder > .ant-tabs-content,
.copilot-tabs .ant-tabs-tabpane { height: 100%; }
`

const mapProfilesToV5Templates = (profiles) => {
  if (!Array.isArray(profiles)) return []
  return profiles
    .map((profile) => {
      const insightJson = extractInsightJson(profile)
      if (!insightJson) return null
      const template = buildCopilotTabsTemplateData(insightJson)
      return template?.analysis ? template : null
    })
    .filter(Boolean)
}

const SectionTitle = ({ children }) => (
  <Flex align='center' gap={7} style={{ marginBottom: 12 }}>
    <Badge color='#1ba784' />
    <Typography.Text strong>{children}</Typography.Text>
  </Flex>
)

const CopilotTabs = ({ basicContent, templateData = emptyCopilotTabsTemplateData, relatedOrders = [], relatedOrdersLoading = false, relatedOrdersError = '', coliSN = '', opiSn = 0 }) => {
  const [activeKey, setActiveKey] = useState('user')
  const [relatedOrdersActiveKeys, setRelatedOrdersActiveKeys] = useState([])
  const [relatedOrderProfiles, setRelatedOrderProfiles] = useState({})
  const [editCustomerRequestOpen, setEditCustomerRequestOpen] = useState(false)
  const [savingCustomerRequest, setSavingCustomerRequest] = useState(false)
  const { user, orderDetailLines = [], analysis, updatedAt = '' } = templateData || {}

  const { message } = App.useApp()
  const [editCustomerRequestForm] = Form.useForm()
  const fetchOrderProfile = useCustomerProfileStore((state) => state.fetchOrderProfile)
  const updateCustomerRequest = useOrderStore((state) => state.updateCustomerRequest)

  const handleEditCustomerRequest = useCallback((values) => {
    setSavingCustomerRequest(true)
    updateCustomerRequest(coliSN, opiSn, values.customerRequest)
      .then(() => {
        message.success('保存成功')
        setEditCustomerRequestOpen(false)
      })
      .catch((reason) => {
        message.error(reason?.message || '保存失败')
      })
      .finally(() => setSavingCustomerRequest(false))
  }, [coliSN, opiSn, updateCustomerRequest, message])

  const loadRelatedOrderProfile = useCallback((order) => {
    const orderSn = order.orderSn
    if (!orderSn) return
    setRelatedOrderProfiles((prev) => ({ ...prev, [orderSn]: { loading: true, error: '', data: null } }))
    fetchOrderProfile(orderSn)
      .then((result) => {
        setRelatedOrderProfiles((prev) => ({ ...prev, [orderSn]: { loading: false, error: '', data: result } }))
      })
      .catch((reason) => {
        setRelatedOrderProfiles((prev) => ({ ...prev, [orderSn]: { loading: false, error: reason?.message || '画像加载失败', data: null } }))
      })
  }, [fetchOrderProfile])

  const handleRelatedOrdersChange = useCallback((keys) => {
    const newlyOpened = keys.filter((key) => !relatedOrdersActiveKeys.includes(key))
    setRelatedOrdersActiveKeys(keys)
    newlyOpened.forEach((key) => {
      const order = relatedOrders.find((item) => String(item.key) === key)
      if (order && !relatedOrderProfiles[order.orderSn]) {
        loadRelatedOrderProfile(order)
      }
    })
  }, [relatedOrders, relatedOrdersActiveKeys, relatedOrderProfiles, loadRelatedOrderProfile])

  const hasProfile = Boolean(analysis)

  const renderUserTab = () => !hasProfile ? (
    <div style={{ padding: 10 }}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='暂无客户画像' />
    </div>
  ) : (
    <div style={{ padding: 10 }}>
      <Card size='small' variant='outlined'>
        <Flex gap={12} align='center'>
          <Avatar size={48} style={{ backgroundColor: '#1ba784', flexShrink: 0 }}>{user.avatarText}</Avatar>
          <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
            <Typography.Text strong style={{ fontSize: 16 }}>{user.name}</Typography.Text>
          </Flex>
        </Flex>
        <Flex wrap gap={6} align='center' style={{ marginTop: 13 }}>
          {user.orderId && <Tag>{user.orderId}</Tag>}
          {user.whatsappPhoneNumber && <Typography.Text type='secondary'>电话：{user.whatsappPhoneNumber}</Typography.Text>}
          {user.country && <Tag color='blue'>{user.country}</Tag>}
          {user.travelerSummary && <Typography.Text type='secondary'>{user.travelerSummary}</Typography.Text>}
          {user.customerType && <Typography.Text type='secondary'>{user.customerType}</Typography.Text>}
          {user.duration && <Typography.Text type='secondary'>{user.duration}</Typography.Text>}
          {user.departureDate && <Typography.Text type='secondary'>{user.departureDate}</Typography.Text>}
        </Flex>
        <Divider style={{ margin: '13px 0 9px' }} />
        <Divider plain style={{ margin: '0 0 12px' }}>客人要求</Divider>
        <Flex justify='space-between' align='flex-start' gap={8}>
          <Flex vertical flex={1} style={{ minWidth: 0 }}>
            {user.customerRequest && <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>{user.customerRequest}</Typography.Text>}
          </Flex>
          <Tooltip title='编辑客人要求'>
            <span>
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                aria-label='编辑客人要求'
                onClick={() => {
                  editCustomerRequestForm.setFieldsValue({ customerRequest: user.customerRequest })
                  setEditCustomerRequestOpen(true)
                }}
              />
            </span>
          </Tooltip>
        </Flex>
      </Card>
    </div>
  )

  const renderRelatedOrderHeader = (order) => (
    <Flex vertical gap={4}>
      <Flex justify='space-between' align='start' gap={8}>
        <Flex align='center' gap={6} wrap style={{ minWidth: 0 }}>
          <Typography.Text strong>{order.orderNo}</Typography.Text>
          {order.quotationName && <Typography.Text type='secondary'>{order.quotationName}</Typography.Text>}
        </Flex>
        <Tag color={order.statusType}>{order.status || '未知'}</Tag>
      </Flex>
      <Flex wrap gap='4px 10px'>
        {order.dateRange && <Typography.Text type='secondary'>{order.dateRange}</Typography.Text>}
        {order.peopleText && <Typography.Text type='secondary'>{order.peopleText}</Typography.Text>}
        {order.customerType && <Typography.Text type='secondary'>{order.customerType}</Typography.Text>}
        {order.daysText && <Typography.Text type='secondary'>{order.daysText}</Typography.Text>}
      </Flex>
      {order.orderMl != null && order.orderMl !== '' && <Typography.Text type='danger'>毛利：{order.orderMl}</Typography.Text>}
    </Flex>
  )

  const renderRelatedOrderProfile = (order) => {
    const profile = relatedOrderProfiles[order.orderSn]
    if (profile?.loading) return <Skeleton active paragraph={{ rows: 3 }} />
    if (profile?.error) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={profile.error} />
    if (!profile) return null
    const templates = mapProfilesToV5Templates(profile.data)
    if (templates.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='暂无画像数据' />
    return templates.map((template, index) => (
      <Copilot5V key={`V5-${index}`} data={template.analysis} updatedAt={template.updatedAt} showHeader={false} />
    ))
  }

  const renderRelatedOrdersTab = () => (
    <div style={{ padding: 10 }}>
      <SectionTitle>关联订单</SectionTitle>
      {relatedOrdersLoading && <Skeleton active paragraph={{ rows: 3 }} />}
      {!relatedOrdersLoading && relatedOrdersError && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={relatedOrdersError} />}
      {!relatedOrdersLoading && !relatedOrdersError && relatedOrders.length > 0 && (
        <Collapse
          bordered
          activeKey={relatedOrdersActiveKeys}
          onChange={handleRelatedOrdersChange}
          items={relatedOrders.map((order) => ({
            key: order.key,
            label: renderRelatedOrderHeader(order),
            children: renderRelatedOrderProfile(order),
          }))}
        />
      )}
      {!relatedOrdersLoading && !relatedOrdersError && relatedOrders.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='无关联订单' />}
    </div>
  )

  const renderOrderDetailLines = () => (
    <Flex vertical gap={4}>
      {orderDetailLines.filter((line) => line && !/^=+$/.test(line)).map((line, index) => (
        <Typography.Text key={index}>{line}</Typography.Text>
      ))}
    </Flex>
  )

  const renderFormTab = () => (
    <div style={{ padding: 10 }}>
      <Card size='small' variant='outlined'>
        <SectionTitle>表单信息</SectionTitle>
        {orderDetailLines.length > 0 && renderOrderDetailLines()}
        {orderDetailLines.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='无表单信息' />}
      </Card>
    </div>
  )

  return (
    <>
      <style>{TABS_LAYOUT_CSS}</style>
      <Tabs
        className='copilot-tabs'
        size='small'
        type='card'
        activeKey={activeKey}
        onChange={setActiveKey}
        style={{ display: 'flex', height: '100%', flexDirection: 'column', background: '#fff' }}
        tabBarStyle={{ margin: 0, padding: '8px 8px 0', overflowX: 'auto', overflowY: 'hidden' }}
        items={[
          { key: 'user', label: '用户信息', children: renderUserTab() },
          { key: '5v', label: '5V价值', children: hasProfile ? <Copilot5V data={analysis} updatedAt={updatedAt} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='暂无 V5 画像数据' /> },
          { key: 'orders', label: '关联订单', children: renderRelatedOrdersTab() },
          { key: 'form', label: '表单信息', children: renderFormTab() },
          { key: 'basic', label: '基础信息', children: basicContent },
        ]}
      />
      <Modal
        title='编辑客人要求'
        open={editCustomerRequestOpen}
        onOk={() => editCustomerRequestForm.submit()}
        onCancel={() => setEditCustomerRequestOpen(false)}
        confirmLoading={savingCustomerRequest}
        okText='保存'
        cancelText='取消'
      >
        <Form form={editCustomerRequestForm} layout='vertical' onFinish={handleEditCustomerRequest}>
          <Form.Item name='customerRequest' label='客人要求' rules={[{ required: true, message: '请输入客人要求' }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default CopilotTabs
